import Button from "@rio-cloud/rio-uikit/Button"
import Dialog from "@rio-cloud/rio-uikit/Dialog"
import FilePicker from "@rio-cloud/rio-uikit/FilePicker"
import Spinner from "@rio-cloud/rio-uikit/Spinner"
import { ReactElement, useState } from "react"
import { saveAs } from "file-saver"
import { FormattedMessage } from "react-intl"
import { createConfirmationUploadTemplate } from "./confirmationUploadTemplate"
import {
    ConfirmationUploadProcessor,
    ProcessedUpload,
    UploadResult,
    useConfirmationUploadProcessor,
} from "./confirmationUploadProcessor"

import type { ConfirmationUploadType } from "./confirmationUploadCsv"

interface ConfirmationUploadDialogProps {
    uploadType: ConfirmationUploadType
    show: boolean
    onClose: () => void
    processor?: ConfirmationUploadProcessor
}

type SelectingDialogState = { status: "selecting", selectedFile: File | null };
type ProcessingDialogState = { status: "processing", selectedFile: File, results: UploadResult[] };
type CompletedDialogState = {
    status: "completed"
    selectedFile: File
    upload: ProcessedUpload
    reportedResults: UploadResult[]
    processorFailed: boolean
};
type ConfirmationUploadDialogState =
    | SelectingDialogState
    | ProcessingDialogState
    | CompletedDialogState


interface FileSelectionContentProps {
    instructionId: string
    selectedFile: File | null
    onFilePicked: (file: File | null) => void
    onRemoveSelectedFile: () => void
}

const FileSelectionContent = ({
    instructionId,
    selectedFile,
    onFilePicked,
    onRemoveSelectedFile,
}: FileSelectionContentProps): ReactElement => {
    return <>
        <p className="margin-bottom-20"><FormattedMessage id={instructionId}/></p>
        <div>
            <FilePicker
                accept={{ "text/csv": [".csv"] }}
                displayMode="dropzone"
                multiple={false}
                onPick={(files): void => onFilePicked(files[0] ?? null)}
            >
                {({ isDragActive }) => <div
                    className="rounded border border-style-dashed border-color-gray bg-lightest padding-20 text-center text-color-darker"
                >
                    <span className="rioglyph rioglyph-csv text-size-h1 opacity-50"/>
                    <div><FormattedMessage
                        id={isDragActive
                            ? "transportOrder.confirmationUpload.dropzone.active"
                            : "transportOrder.confirmationUpload.dropzone.inactive"}
                    /></div>
                </div>}
            </FilePicker>
            {selectedFile !== null && <div
                className="display-flex align-items-center justify-content-between rounded border border-color-gray padding-20 margin-top-10"
            >
                <div className="text-medium text-color-darker">{selectedFile.name}</div>
                <Button
                    bsSize={"xs"}
                    bsStyle={"muted"}
                    data-testid="confirmation-upload-remove-selected-file"
                    iconOnly
                    iconName="rioglyph-remove"
                    onClick={onRemoveSelectedFile}
                />
            </div>}
        </div>
    </>
}

interface FileSelectionActionsProps {
    selectedFile: File | null
    onDownloadTemplate: () => void
    onCancel: () => void
    onStartProcessing: () => void
}

const FileSelectionActions = (props: FileSelectionActionsProps): ReactElement => <>
    <Button onClick={props.onDownloadTemplate}>
        <span className="rioglyph rioglyph-download"/>
        <FormattedMessage id="transportOrder.confirmationUpload.action.downloadTemplate"/>
    </Button>
    <div className="flex-1-1"/>
    <Button onClick={props.onCancel}>
        <FormattedMessage id="transportOrder.confirmationUpload.action.cancel"/>
    </Button>
    <Button
        bsStyle={"primary"}
        className="margin-left-10"
        disabled={props.selectedFile === null}
        iconRight
        onClick={props.onStartProcessing}
    >
        <FormattedMessage id="transportOrder.confirmationUpload.action.next"/>
        <span className="rioglyph rioglyph-chevron-right"/>
    </Button>
</>

interface UploadFailureAlertProps {
    results: readonly UploadResult[]
    processorFailed?: boolean
}

const UploadFailureAlert = (props: UploadFailureAlertProps): ReactElement | null => {
    const failedResults = props.results
        .filter(result => result.status === "ERROR")
        .sort((first, second) => first.sourceLine - second.sourceLine)
    if (failedResults.length === 0 && !props.processorFailed) {
        return null
    }

    return <div
        className="alert alert-warning margin-top-20 max-height-300 overflow-y-auto text-left"
        role="alert"
    >
        <div className="display-flex gap-10">
            <span className="rioglyph rioglyph-exclamation-sign text-size-h4 text-color-warning"/>
            <div>
                {failedResults.length > 0 && <>
                    <div className="text-medium">
                        <FormattedMessage
                            id="transportOrder.confirmationUpload.warning.summary"
                            values={{ failureCount: failedResults.length }}
                        />
                    </div>
                    <ul className="list-unstyled margin-bottom-0">
                        {failedResults.map(result => <li className="padding-y-5" key={result.sourceLine}>
                            <FormattedMessage
                                id="transportOrder.confirmationUpload.result.line"
                                values={{ line: result.sourceLine }}
                            />{" "}
                            {result.errorReason ?? <FormattedMessage
                                id="transportOrder.confirmationUpload.result.unreadable"
                            />}
                        </li>)}
                    </ul>
                </>}
                {props.processorFailed && <div>
                    <FormattedMessage id="transportOrder.confirmationUpload.warning.fileError"/>
                </div>}
            </div>
        </div>
    </div>
}

interface ProcessingStatusProps {
    results: readonly UploadResult[]
}

const ProcessingStatus = ({
    results,
}: ProcessingStatusProps): ReactElement => <div
    className="padding-y-40 text-center"
    role="status"
>
    <Spinner isDoubleSized/>
    <div className="margin-top-20 text-medium">
        <FormattedMessage id="transportOrder.confirmationUpload.processing"/>
    </div>
    <UploadFailureAlert results={results}/>
</div>


type CompletedConfirmationUploadDialogState = Extract<ConfirmationUploadDialogState, { status: "completed" }>

interface UploadCompletionContentProps {
    state: CompletedConfirmationUploadDialogState
}

const UploadCompletionContent = ({
    state,
}: UploadCompletionContentProps): ReactElement => {
    const successfulCount = state.upload.results.filter(result => result.status === "SUCCESS").length
    const failureResults = state.reportedResults.some(result => result.status === "ERROR")
        ? state.reportedResults
        : state.upload.results

    return <>
        <div className="text-center">
            <div className="text-size-18 margin-bottom-25">
                <FormattedMessage id="transportOrder.confirmationUpload.completed.heading"/>
            </div>
            <span
                style={{ fontSize: "60px" }}
                className="rioglyph rioglyph-ok-sign text-color-primary"
            />
            <p className="margin-top-25">
                <FormattedMessage
                    id="transportOrder.confirmationUpload.completed.summary"
                    values={{
                        successfulCount,
                        totalCount: state.upload.results.length,
                    }}
                />
            </p>
        </div>
        <UploadFailureAlert
            results={failureResults}
            processorFailed={state.processorFailed}
        />
    </>
}

interface ConfirmationUploadStepProps {
    uploadType: ConfirmationUploadType
    state: ConfirmationUploadDialogState
    onFilePicked: (file: File | null) => void
    onRemoveSelectedFile: () => void
    onDownloadTemplate: () => void
    onCancel: () => void
    onStartProcessing: () => void
    onBack: () => void
    onDone: () => void
}

const ConfirmationUploadStep = ({
    uploadType,
    state,
    onFilePicked,
    onRemoveSelectedFile,
    onDownloadTemplate,
    onCancel,
    onStartProcessing,
    onBack,
    onDone,
}: ConfirmationUploadStepProps): ReactElement => {
    const translationKeysMap: Record<ConfirmationUploadType, { titleId: string, instructionId: string }> = {
        MATERIAL: {
            titleId: "transportOrder.confirmationUpload.dialog.material.title",
            instructionId: "transportOrder.confirmationUpload.dialog.material.instruction",
        },
        EMPTIES: {
            titleId: "transportOrder.confirmationUpload.dialog.empties.title",
            instructionId: "transportOrder.confirmationUpload.dialog.empties.instruction",
        },
    }

    const translationKeys = translationKeysMap[uploadType]
    let step: ReactElement

    switch (state.status) {
    case "selecting":
        step = <>
            <Dialog.Body>
                <FileSelectionContent
                    instructionId={translationKeys.instructionId}
                    selectedFile={state.selectedFile}
                    onFilePicked={onFilePicked}
                    onRemoveSelectedFile={onRemoveSelectedFile}
                />
            </Dialog.Body>
            <Dialog.Footer className="display-flex">
                <FileSelectionActions
                    selectedFile={state.selectedFile}
                    onDownloadTemplate={onDownloadTemplate}
                    onCancel={onCancel}
                    onStartProcessing={onStartProcessing}
                />
            </Dialog.Footer>
        </>
        break
    case "processing":
        step = <>
            <Dialog.Body>
                <ProcessingStatus
                    results={state.results}
                />
            </Dialog.Body>
            <Dialog.Footer className="display-flex">
                <div className="flex-1-1"/>
                <Button disabled onClick={onCancel}>
                    <FormattedMessage id="transportOrder.confirmationUpload.action.cancel"/>
                </Button>
            </Dialog.Footer>
        </>
        break
    case "completed":
        step = <>
            <Dialog.Body>
                <UploadCompletionContent
                    state={state}
                />
            </Dialog.Body>
            <Dialog.Footer className="display-flex">
                <Button onClick={onBack}>
                    <FormattedMessage id="transportOrder.confirmationUpload.action.back"/>
                </Button>
                <div className="flex-1-1"/>
                <Button bsStyle={"primary"} onClick={onDone}>
                    <FormattedMessage id="transportOrder.confirmationUpload.action.done"/>
                </Button>
            </Dialog.Footer>
        </>
        break
    default: {
        return state
    }
    }

    return <>
        <Dialog.Title title={<FormattedMessage id={translationKeys.titleId}/>}/>
        {step}
    </>
}

export const ConfirmationUploadDialog = (props: ConfirmationUploadDialogProps): ReactElement => {
    const defaultProcessor = useConfirmationUploadProcessor(props.uploadType)
    const processor = props.processor ?? defaultProcessor
    const [dialogState, setDialogState] = useState<ConfirmationUploadDialogState>({
        status: "selecting",
        selectedFile: null,
    })

    const downloadTemplate = (): void => {
        const template = createConfirmationUploadTemplate(props.uploadType)
        saveAs(new Blob([template.content], { type: "text/csv;charset=utf-8" }), template.fileName)
    }

    const closeDialog = (): void => {
        if (dialogState.status === "processing") {
            return
        }
        setDialogState({ status: "selecting", selectedFile: null })
        props.onClose()
    }

    const startProcessing = (): void => {
        if (dialogState.status !== "selecting" || dialogState.selectedFile === null) {
            return
        }
        const selectedFile = dialogState.selectedFile

        setDialogState({ status: "processing", selectedFile, results: [] })

        const processUpload = async (): Promise<void> => {
            let completedUpload: ProcessedUpload
            let processorFailed = false

            try {
                completedUpload = await processor.process(selectedFile, result => {
                    setDialogState(currentState => currentState.status === "processing"
                        ? { ...currentState, results: [...currentState.results, result] }
                        : currentState)
                })
            } catch {
                processorFailed = true
                completedUpload = {
                    results: [],
                }
            }

            setDialogState(currentState => currentState.status === "processing"
                ? {
                    status: "completed",
                    selectedFile,
                    upload: completedUpload,
                    reportedResults: currentState.results,
                    processorFailed,
                }
                : currentState)
        }

        void processUpload()
    }

    const removeSelectedFile = (): void => {
        setDialogState({ status: "selecting", selectedFile: null })
    }

    const pickFile = (file: File | null): void => {
        setDialogState({ status: "selecting", selectedFile: file })
    }

    const returnToFileSelection = (): void => {
        if (dialogState.status !== "completed") {
            return
        }
        setDialogState({
            status: "selecting",
            selectedFile: dialogState.selectedFile,
        })
    }

    return <Dialog
        disableEsc={dialogState.status === "processing"}
        onClose={closeDialog}
        showCloseButton={dialogState.status !== "processing"}
        show={props.show}
    >
        <ConfirmationUploadStep
            uploadType={props.uploadType}
            state={dialogState}
            onFilePicked={pickFile}
            onRemoveSelectedFile={removeSelectedFile}
            onDownloadTemplate={downloadTemplate}
            onCancel={closeDialog}
            onStartProcessing={startProcessing}
            onBack={returnToFileSelection}
            onDone={closeDialog}
        />
    </Dialog>
}
